// ABOUTME: Per-chapter point-tag anchor coordinates extracted from Figma (SPEC-26 Phase 3 → applied Phase 4).
// ABOUTME: Native-frame pixels; useVideoBox maps them onto the cropped video at runtime.

// Coordinates are in the authored frame's own pixel space, relative to that frame's top-left, exactly
// as recorded in versai-frontend/DESIGN.md §3. They are NOT percentages and NOT viewport-relative —
// `videoBoxMath` converts them, accounting for object-fit:cover cropping.
//
// Keys are raw catalogue attribute keys (`Power_Reserve`, `crystal`, ...) because that is what
// `attributeMatcher` emits and what the backend's `reel_tag_eligibility` map is keyed by. A tag only
// ever renders when the key is BOTH eligible for the chapter (backend config) and has an authored
// anchor here (design). This module is the design half of that pair and deliberately contains no
// eligibility logic of its own.
//
// ── THE 2026-09-04 RE-AUTHOR ────────────────────────────────────────────────
// Every desktop frame was redrawn: the two-part plates are gone, replaced by single-line
// 135.667-tall callouts, and the SET of tags changed with them. `watch_360` gained three tags where
// it had none, `dial` swapped its two editorial prose plates for three spec ones, `crown` swapped
// one, and `movement` now carries five at once. The whole table below is a fresh transcription of
// those frames; only `exploded` is untouched (see its note).
//
// Since every tagged chapter is STATIC now (staticChapterTags.js), several keys here are synthetic
// — `Frequency`, `Escapement`, `Case_Size`, `Edge_Finish`, `Lug_Construction`, `Strap`,
// `Dial_Pattern`. No backend eligibility map emits them and the catalogue holds no such attribute;
// they exist to name a position the design draws. The keys that ARE real catalogue attributes
// (`Power_Reserve`, `Movement`, `crystal`, `Case_Finish`, `Case_Material`, `Dial_Finish`, `Hands`,
// `Hand_Style`) are kept as they were, so re-enabling the dynamic path for a chapter is a one-line
// deletion in staticChapterTags.js rather than a re-keying exercise.
//
// `idle` (45:1371) and `collection` (45:1143) author no tags at all — re-checked 2026-09-04. Idle
// is bare film; collection shows a variant swatch row, which is CollectionOverlay's surface.
//
// COORDINATES are frame-relative and normalised against the frame's own backdrop image, which sits
// at a sub-pixel offset on two frames (movement's at x=-0.664, crown's at +0.336). Normalising
// keeps a coordinate measured against the PICTURE, which is what videoBoxMath maps; the correction
// is under 0.7px in a 3840-wide frame, so it changes nothing visible and only stops the two frames
// disagreeing with the other three about where zero is.
const DESKTOP_FRAME = { width: 3840, height: 2160 };
const MOBILE_FRAME = { width: 1216, height: 2160 };

// Every re-authored plate is the same 135.667 tall (padding 53.333 + one 42px line, cap-trimmed —
// see PointTag's COMPOSITION note), rounded here like every other coordinate in the table.
const PLATE_H = 136;

const DESKTOP_ANCHORS = {
  // 45:1518. Vertically stacked down the right third, ~390px apart — no pair is close enough to
  // need `clip`, and none of the three shares an x-range with a neighbour's box.
  watch_360: {
    Strap: { x: 2570, y: 640, w: 372, h: PLATE_H },              // 369:606 "Leather Strap"
    crystal: { x: 2723, y: 1029, w: 909, h: PLATE_H },           // 369:610 "Domed Sapphire / Double ARDUR Crystal"
    Lug_Construction: { x: 2570, y: 1426, w: 815, h: PLATE_H },  // 369:608 "Independently Welded Teardrop Lug"
  },
  // 45:2121. Two columns — right (471/994/1517), left (581/1248) — every gap over 500px, so the
  // `clip: true` the old two-part Movement plate needed is gone with the collision that caused it.
  movement: {
    Power_Reserve: { x: 2496, y: 471, w: 545, h: PLATE_H },   // 369:107 "65 Hour Power Reserve" (SPEC-54, 2026-09-29 datasheet; Figma still draws "86 Hour Power Reserve")
    Movement: { x: 529, y: 581, w: 832, h: PLATE_H },         // 369:113 "Vaucher VMF 5500 Micro-Rotor" (was "Vaucher MFV 5500" until 2026-09-29; corrected 2026-09-15, was "Schwarz Etienne ASE 200 Micro-Rotor")
    Frequency: { x: 2571, y: 994, w: 748, h: PLATE_H },       // 369:111 "28,800 vph Movement Frequency"
    Escapement: { x: 644, y: 1248, w: 574, h: PLATE_H },      // 369:109 "Machine Chamfering" (SPEC-54; Figma still draws "Swiss Lever Escapement")
    crystal: { x: 2296, y: 1517, w: 603, h: PLATE_H },        // 369:105 "Flat Sapphire Caseback" (SPEC-54; Figma still draws "Sapphire Display Window")
  },
  // 45:1818. All three in the right half, 625 and 556px apart — no clip.
  crown: {
    Case_Finish: { x: 2520, y: 387, w: 997, h: PLATE_H },     // 369:88 "Stainless steel, hand-polished by a craftsman."
    Case_Material: { x: 2820, y: 1012, w: 472, h: PLATE_H },  // 369:92 "316L Stainless Steel"
    Case_Size: { x: 2520, y: 1568, w: 630, h: PLATE_H },      // 369:90 "39mm Case (38.5mm Bezel)"
  },
  // 45:1668. `Dial_Finish` is the only one on the left; the other two are right-side and 900px
  // apart. The old `clip: true` belonged to a 315-tall plate that no longer exists.
  dial: {
    Hands: { x: 2350, y: 341, w: 829, h: PLATE_H },           // 369:583 "Diamond-Cut Hands" (SPEC-54; Figma still draws "Diamond Cut Rhodium-Plated Hands")
    Dial_Finish: { x: 514, y: 752, w: 614, h: PLATE_H },      // 369:581 "Polished 3D Micro-Indexes"
    Edge_Finish: { x: 2591, y: 1241, w: 716, h: PLATE_H },    // 369:579 "Polished, Chamfered Rings" (SPEC-54; Figma still draws "Polished with Chamfered Edges")
  },
  // 45:1968 — DELIBERATELY NOT re-transcribed. The frame now sets its three plates at
  // (724,1205)/(1075,1397)/(1346,1589) and these are the shipped, signed-off positions from
  // 2026-08-28; the client's instruction on 2026-09-04 was "exploded is already done and set", so
  // the drift is recorded here and not taken. Update these three together with a fresh sign-off,
  // not as a drive-by consistency fix.
  //
  // The heights are the pre-redesign plate sizes for the same reason, and they are NOT inert: a
  // single-line tag renders 136 native px, so `platesClash` (tagPlacement.js) reasons about
  // avoid-rects up to 124px taller than what is drawn. That only matters to the region cascade,
  // which never runs on a static chapter — so it costs nothing today and would need correcting
  // first if `exploded` were ever handed back to the dynamic path.
  exploded: {
    Hand_Style: { x: 890, y: 1142, w: 347, h: 259 },   // "Diamond-Cut Hands"
    Dial_Finish: { x: 1300, y: 1348, w: 370, h: 260 }, // "3D Micro Indexes"
    // Was `Guilloche_Dial`. The copy stopped saying guilloché on 2026-08-28 (it is a Vélos dial,
    // in no Vesper record) and the key stopped saying it on 2026-09-04. Synthetic either way: no
    // eligibility map emits it, so only the static path can reach this position.
    Dial_Pattern: { x: 1810, y: 1516, w: 456, h: 233 }, // "Multilayered Dial" (SPEC-54; was "Geometric patterned dial")
  },
};

// Phase 4b (2026-08-13). Mobile is a structurally different composition, so it gets its own
// authored table rather than scaled desktop values. Two things about it differ in kind, not degree:
//
//  1. **The x/y are not positions to render at.** The mobile design bottom-anchors every chapter's
//     tags as one horizontal ROW above the CC bar (verified on 45:2947 / 45:3062 / 45:3179 /
//     45:3294 — every tag in a chapter shares a y, and the row is inset symmetrically). So x is read
//     only for the left-to-right ORDER of the row and w/h only as the authored box size; the row
//     itself is laid out by flow, from `mobileGeometry.TAG_ROW`. Keeping the full box here anyway
//     means the table stays a faithful transcription of the frames, and the bounds test can still
//     prove every coordinate lands inside the authored frame.
//  2. **No crop maths applies.** Desktop anchors point at parts of the watch and therefore have to
//     track the object-fit:cover crop; a bottom-anchored row is chrome, and tracks the viewport.
//
// Key→position assignments mirror the desktop table's exactly, so a key never means one feature on
// desktop and another on mobile.
//
// ── WHAT 2026-09-04 CHANGED HERE, AND WHAT IT DID NOT ───────────────────────
// The mobile frames were NOT re-authored (all five re-read that day: 45:2834, 45:2947, 45:3062,
// 45:3179, 45:3294 all still draw the pre-redesign rows). The client's instruction is that mobile
// says what desktop says, so the COPY comes from the desktop frames (staticChapterTags.js) and
// these boxes are re-sized to fit it. Three consequences, all deliberate:
//
//   • `dial` and `crown` gain a third slot, and `watch_360` gains a row it never had. Inventing a
//     box is normally forbidden in this table — but only because a WRONG desktop coordinate points
//     at the wrong part of the watch. Mobile has no such failure mode: the row is bottom-anchored
//     chrome laid out by flow, so a synthesized box costs nothing but its order.
//   • Boxes whose copy changed are re-sized from it (≈18.5 authored px per character plus the
//     plate's 2×45 padding — calibrated against the four authored rows, which land within ~5%).
//     They are estimates of a rendered width, not transcriptions, and the row self-corrects anyway:
//     the plates are flex children with `min-w-0`, so a mis-estimate re-wraps rather than overflows.
//   • `x` still encodes ORDER only, and the re-authored rows tile it (40 inset, 10 gap — TAG_ROW)
//     so the table reads as the row it lays out. `exploded` keeps its authored x/w/h untouched,
//     like its desktop counterpart.
//
// `movement` is the one chapter where mobile shows FEWER tags than desktop: five plates do not fit
// a 1216-wide row (the three authored widths already fill it exactly), so `Frequency` and
// `Escapement` have no mobile position and staticChapterTags.js gives them no mobile copy.
// `y` is derived, not chosen: the row shares ONE baseline (the invariant this table's test pins),
// so a plate that carries three lines instead of two starts higher rather than hanging below its
// neighbours. y = the chapter's authored row bottom - this plate's height.
const MOBILE_ANCHORS = {
  watch_360: {                                                   // synthesized — 45:2834 authors none; row bottom 1847
    Strap: { x: 40, y: 1675, w: 220, h: 172 },                   // "Leather / Strap"
    crystal: { x: 270, y: 1634, w: 386, h: 213 },                // "Domed Sapphire / / Double ARDUR / Crystal"
    Lug_Construction: { x: 666, y: 1634, w: 331, h: 213 },       // "Independently / Welded / Teardrop Lug"
  },
  movement: {                                                    // 45:3294 — authored x/w kept; row bottom 1847
    Power_Reserve: { x: 40, y: 1690, w: 338, h: 157 },           // "65 Hour / Power Reserve" (was "86 Hour" until 2026-09-29)
    Movement: { x: 388, y: 1634, w: 372, h: 213 },               // "Vaucher / VMF 5500 / Micro-Rotor" (corrected 2026-09-15; MFV -> VMF 2026-09-29)
    crystal: { x: 770, y: 1675, w: 349, h: 172 },                // "Flat Sapphire / Caseback" (SPEC-54; was "Sapphire / Display Window")
  },
  crown: {                                                       // 45:3179 — Case_Finish authored; row bottom 1851
    Case_Finish: { x: 37, y: 1643, w: 367, h: 208 },             // "Stainless steel, / hand-polished / by a craftsman."
    Case_Material: { x: 414, y: 1679, w: 349, h: 172 },          // "316L Stainless / Steel"
    Case_Size: { x: 773, y: 1679, w: 349, h: 172 },              // "39mm Case / (38.5mm Bezel)"
  },
  dial: {                                                        // 45:2947 — re-ordered to the desktop frame's reading order; row bottom 1847
    Hands: { x: 40, y: 1634, w: 349, h: 213 },                   // "Diamond Cut / Hands" (box sized for the old 3-line copy; SPEC-54)
    Dial_Finish: { x: 399, y: 1675, w: 331, h: 172 },            // "Polished 3D / Micro-Indexes"
    Edge_Finish: { x: 740, y: 1675, w: 368, h: 172 },            // "Polished, / Chamfered Rings" (SPEC-54; was "Polished with / Chamfered Edges")
  },
  exploded: {                                                    // 45:3062 — untouched, see desktop table
    Hand_Style: { x: 49, y: 1678, w: 345, h: 176 },              // "Diamond-Cut Hands" (SPEC-27 D14)
    Dial_Finish: { x: 411, y: 1676, w: 306, h: 178 },            // "3D Micro Indexes"  (SPEC-27 D14)
    Dial_Pattern: { x: 734, y: 1676, w: 445, h: 178 },           // "Multilayered / Dial" — static-only (SPEC-54; was "Geometric patterned / dial")
  },
};

const TABLES = { desktop: DESKTOP_ANCHORS, mobile: MOBILE_ANCHORS };

/** The authored frame size for a breakpoint — the coordinate space `anchorForKey` returns points in. */
export function nativeFrameSize(breakpoint) {
  return breakpoint === 'mobile' ? MOBILE_FRAME : DESKTOP_FRAME;
}

/**
 * The authored anchor for one attribute key in one chapter, or null when the design does not place
 * that key in that chapter. Null is a legitimate answer, not an error: callers must skip the tag
 * rather than invent a position (a tag at a guessed coordinate points at the wrong part of the watch,
 * which is worse than no tag).
 *
 * @returns {{x:number, y:number, w:number, h:number}|null}
 */
export function anchorForKey(chapterId, attributeKey, breakpoint = 'desktop') {
  const table = TABLES[breakpoint] || DESKTOP_ANCHORS;
  return table[chapterId]?.[attributeKey] ?? null;
}

// Vertical gap between two tags that resolve to the SAME region, in authored desktop pixels.
// Taken from the pre-2026-09-04 dial frame, which stacked Dial_Finish (y 628) above Hands (y 985)
// in one left-hand column — 357px apart. Those two plates are gone (the re-author scattered dial's
// three across the frame), so this is now a value with no live frame behind it; it is kept because
// it is a reasonable "reads as two tags, not one block" distance and because the whole region
// cascade is dormant while every chapter is static. Re-derive it from a real frame before leaning
// on the cascade again.
const REGION_STACK_GAP = 357;
// Horizontal offset per stack step. The design never sets two tags in a dead-straight column when
// it scatters several over one frame: movement (45:2121) steps its left column right by 203px
// (Movement x1064 → Power_Reserve x1267) as it descends. A straight same-x stack of same-width
// region boxes read as one repeated plate ("all on the left, same size" — reported 2026-08-27).
// The stack ZIGZAGS (left first, then right, widening) rather than marching one direction: the
// 2026-08-27 region rebalance moved every fallback base into the right half, and a rightward-only
// diagonal from there left the authored frame after one or two steps — the probe then found no
// fresh slot and fell back to reusing a spent one, which is two tags at the same pixel (the
// "on top of each other" report, 2026-08-27 evening). Alternating sides keeps every step on the
// frame AND gives the scattered look of the multi-tag Figma frames instead of a column.
const REGION_STACK_INDENT = 203;

/**
 * The anchor for one attribute key, resolved through the backend's region map when the design
 * never authored a per-key position for it (SPEC-27 P13).
 *
 * WHY THIS EXISTS. `anchorForKey` above can only answer for the nine positions a designer drew,
 * so eighteen other catalogue attributes could never surface no matter how clearly the agent
 * named them. The client's requirement is the 2D highlighter's behaviour — mention an attribute,
 * it appears — which means every physically visible attribute needs a position, not just the
 * illustrated ones. `tagRegions` supplies that: the chapter says where each component sits, and
 * `attributeRegion` says which component an attribute belongs to.
 *
 * PRECEDENCE. An authored per-key anchor always wins; it is a designer's judgement about that
 * exact plate in that exact frame, and the region map is a rule. Only when there is no authored
 * anchor does the region answer.
 *
 * STACKING. Several attributes legitimately share a component ("what colour is the dial" and
 * "what finish is the dial" are both `dial_face`). `stackIndex` cascades them downward from the
 * region anchor so the second does not land on top of the first — the same column the dial frame
 * already uses. Callers pass the tag's index among the region-derived tags for that region.
 *
 * @returns {{x:number, y:number, w:number, h:number}|null} null means "do not place this" —
 *   an unknown key, an abstract attribute with no component, or a component not in this frame.
 */
export function resolveAnchor(chapterId, attributeKey, breakpoint = 'desktop', tagRegions = null, stackIndex = 0) {
  const authored = anchorForKey(chapterId, attributeKey, breakpoint);
  if (authored) return authored;
  // Mobile is a bottom-anchored flow row, not a positioned layer — coordinates are meaningless
  // there and the region map deliberately carries none. Returning null would suppress the tag;
  // MobileTagRow only needs to know the key is placeable, so hand back the region box as-is.
  const region = tagRegions?.regions?.[chapterId]?.[tagRegions?.attributeRegion?.[attributeKey]];
  if (!region) return null;
  if (breakpoint === 'mobile') return region;
  // Zigzag: step 1 goes left of the base, step 2 right, step 3 further left… (bases favour the
  // right half, so the open ground is usually leftward first). A step that would leave the frame
  // horizontally mirrors to the other side, then clamps — x can always be made legal, so the only
  // way the caller's in-frame check exhausts the stack is the y axis genuinely running out.
  const frame = nativeFrameSize(breakpoint);
  const magnitude = Math.ceil(stackIndex / 2) * REGION_STACK_INDENT;
  const dx = stackIndex % 2 === 1 ? -magnitude : magnitude;
  let x = region.x + dx;
  if (x < 0 || x + region.w > frame.width) x = region.x - dx;
  x = Math.max(0, Math.min(x, frame.width - region.w));
  return {
    ...region,
    x,
    y: region.y + (stackIndex * REGION_STACK_GAP),
  };
}

/** The component an attribute belongs to, or null when it names nothing you can point at. */
export function regionForKey(attributeKey, tagRegions = null) {
  return tagRegions?.attributeRegion?.[attributeKey] ?? null;
}

/**
 * Every wired (chapter, key, anchor) triple for a breakpoint.
 *
 * Exists so callers — and the tests especially — can walk the table itself instead of a
 * hand-maintained list of chapters and keys, which silently stops covering an anchor the moment
 * someone adds one.
 *
 * @returns {Array<{chapterId: string, attributeKey: string, anchor: {x:number,y:number,w:number,h:number}}>}
 */
export function listAnchors(breakpoint = 'desktop') {
  const table = TABLES[breakpoint] || DESKTOP_ANCHORS;
  return Object.entries(table).flatMap(([chapterId, keys]) =>
    Object.entries(keys).map(([attributeKey, anchor]) => ({ chapterId, attributeKey, anchor })));
}

/** How many tags are wired for a chapter — the natural per-chapter cap. Not always the number the
 * design authors: see the recorded mismatches above, where a chapter authors more than it wires. */
export function anchorCountForChapter(chapterId, breakpoint = 'desktop') {
  const table = TABLES[breakpoint] || DESKTOP_ANCHORS;
  return Object.keys(table[chapterId] || {}).length;
}

/** True when a breakpoint has no authored anchors at all. Both are populated as of Phase 4b; the
 * check stays because it is what stops a future third composition silently reusing another's
 * coordinates, which is the failure this whole module exists to prevent. */
export function hasAnchorsForBreakpoint(breakpoint) {
  return Object.keys(TABLES[breakpoint] || {}).length > 0;
}

/**
 * The authored keys of a chapter in left-to-right order — the order the mobile tag row places them
 * in. Derived from the authored x rather than from object key order, because object key order is an
 * artifact of how the table happens to be typed and would silently reorder the row on an edit.
 *
 * Ties (two tags at the same x, which the design never does) fall back to the key name so the order
 * is at least deterministic rather than dependent on insertion.
 *
 * @returns {string[]}
 */
export function orderedKeysForChapter(chapterId, breakpoint = 'desktop') {
  const table = TABLES[breakpoint] || DESKTOP_ANCHORS;
  return Object.entries(table[chapterId] || {})
    .sort(([keyA, a], [keyB, b]) => (a.x - b.x) || keyA.localeCompare(keyB))
    .map(([key]) => key);
}
